import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { useDocumentsListQuery } from 'api';
import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { DOCUMENTS_PAGE_LIMIT } from 'constants/index';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { PostListItem } from 'features/postsList';
import { useAuth, useWindowSize } from 'hooks';
import { Links } from 'settings';
import theme from 'styles/theme';
import { InstanceLevelType, InstanceType } from 'types';
import { getCoreRoute } from 'utils';

import {
  ContainerW,
  EmptyStateTitle,
  PageHeaderW,
  PageTitle,
  PostCardsGrid,
  PostCardWrapper,
  SeeAllLink,
} from './styled';

interface PrivateDocumentsProps {
  employeeId: string;
}

export const PrivateDocuments = ({ employeeId }: PrivateDocumentsProps) => {
  const { isManager, user: currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile, isMacAir } = useWindowSize();

  const isManagerAndNotCurrentUser = isManager && currentUser?.id !== employeeId;
  const { documents } = useDocumentsListQuery({
    type: InstanceType.document,
    level: isManagerAndNotCurrentUser
      ? [InstanceLevelType.shared]
      : [InstanceLevelType.shared, InstanceLevelType.private],
    assigned: isManagerAndNotCurrentUser ? employeeId : '',
    limit: DOCUMENTS_PAGE_LIMIT,
    place: 'profile',
    include_reactions: true,
  });
  const coreRoute = getCoreRoute(location);

  const onNavigateToCreateDocument = () => {
    navigate(
      isManagerAndNotCurrentUser
        ? generatePath(Links.we.employees.employee.createDocument, {
            employeeId,
          })
        : Links.profile.createDocument,
      {
        state: {
          selectPrivate: isManagerAndNotCurrentUser,
          assignedToId: isManagerAndNotCurrentUser ? employeeId : '',
        },
      },
    );
  };

  const onNavigateToPrivateDocumentList = () => {
    navigate(
      generatePath(
        coreRoute === 'profile'
          ? Links.profile.privateDocuments.index
          : Links.we.employees.employee.privateDocuments,
        { employeeId },
      ),
      {
        state: {
          tag: 'private',
          employeeId: employeeId,
        },
      },
    );
  };

  const isShowAddButton = isManager || employeeId === currentUser?.id;
  const displayedDocuments = documents.slice(0, isMacAir && !isMobile ? 2 : 3);

  return (
    <ContainerW>
      <PageHeaderW>
        <PageTitle>{t('profile.privateDocuments.title')}</PageTitle>
        {isShowAddButton && (
          <CreateButton
            startIcon={<AddIcon style={{ fill: theme.palette.white.main }} />}
            onClick={onNavigateToCreateDocument}
            style={{
              height: '56px',
              padding: '16px 32px',
              fontWeight: 400,
              fontSize: '14px',
              backgroundColor: theme.palette.info.main,
              color: theme.palette.common.white,
              borderRadius: '8px',
              textTransform: 'none',
              ...(isMobile && { width: '100%', marginBottom: '16px' }),
            }}
          >
            {t('profile.privateDocuments.buttonText')}
          </CreateButton>
        )}
      </PageHeaderW>
      {documents.length === 0 ? (
        <EmptyStateTitle>{t('profile.privateDocuments.emptyState')}</EmptyStateTitle>
      ) : (
        <>
          <PostCardsGrid>
            {displayedDocuments.map((doc) => (
              <PostCardWrapper key={doc.id}>
                <PostListItem
                  id={doc.id}
                  title={doc.title}
                  description={doc.description}
                  attachments={doc.attachments}
                  author={doc.author}
                  comments_count={doc.comments_count}
                  type={doc.type}
                  meta={doc.meta}
                  poll={doc.poll}
                  published_at={doc.published_at}
                  reactions_stats={doc.reactions_stats}
                  categoriesList={[]}
                  pin={false}
                  isPreview={false}
                  hideAttachments
                />
              </PostCardWrapper>
            ))}
          </PostCardsGrid>
          <SeeAllLink onClick={onNavigateToPrivateDocumentList}>
            {t('profile.privateDocuments.seeAll')}
          </SeeAllLink>
        </>
      )}
    </ContainerW>
  );
};
